/* eslint-disable @typescript-eslint/no-explicit-any */
import type { UseFormReturn } from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';
import { AlertCircle, Info } from 'lucide-react';
import { isValidIpv4 } from '../../../../utils/ip-validation';
import type { SipPeerFormData } from '../schema';

/**
 * Routing tab: peer endpoints (field array) + OPTIONS ping health monitoring.
 *
 * NOTE: `routingConfig` is nullable().optional() in the form schema, so
 * react-hook-form's Path/ArrayPath types don't resolve the deep
 * `routingConfig.endpoints.*` paths. The `as any` casts on the endpoint
 * field-array name and register() paths below are this RHF limitation, not an
 * unmodelled shape — the runtime values are validated by RoutingConfigSchema.
 */
export function RoutingTab({ form }: { form: UseFormReturn<SipPeerFormData> }) {
  const routingConfig = form.watch('routingConfig');
  const optionsPingEnabled = routingConfig?.optionsPingEnabled ?? false;
  const peerType = form.watch('type');
  const registrationRequired = form.watch('registrationRequired');
  const needsStaticEndpoint = !registrationRequired && peerType !== 'softphone' && peerType !== 'webrtc';

  const endpointsArray = useFieldArray({
    control: form.control,
    name: 'routingConfig.endpoints' as any,
  });

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Routing</h3>
        <p className="text-sm text-gray-600 mb-4">
          Configure the remote endpoints SBCLite sends SIP traffic to, and optional OPTIONS health checks.
        </p>
      </div>

      {/* Peer Endpoints (required) */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
        <h4 className="font-medium text-gray-900 flex items-center">
          <Info className="w-5 h-5 mr-2 text-gray-600" />
          Peer Endpoints
        </h4>
        <p className="text-xs text-gray-500">
          Remote addresses SBCLite will send SIP traffic to for this peer. Used for routing and OPTIONS health checks.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Endpoint selection</label>
            <select
              {...form.register('routingConfig.endpointSelectionMethod' as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="round-robin">Round robin</option>
              <option value="weighted">Weighted</option>
              <option value="least-connections">Least connections</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              How to pick an endpoint when multiple are configured.
            </p>
          </div>

          <div className="flex items-end">
            <button
              type="button"
              onClick={() => {
                const existing = form.getValues('routingConfig.endpoints' as any) as any[] | undefined;
                const existingPort = Array.isArray(existing) && typeof existing[0]?.port === 'number' ? existing[0].port : undefined;
                const defaultPort = existingPort ?? 5060;
                endpointsArray.append({ host: '', ip: null, port: defaultPort, weight: 100, enabled: true } as any);
              }}
              className="px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
            >
              Add endpoint
            </button>
          </div>
        </div>

        {needsStaticEndpoint && endpointsArray.fields.length === 0 && (
          <div className="flex items-start p-3 bg-amber-50 border border-amber-200 rounded-md">
            <AlertCircle className="w-5 h-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              Add at least one peer endpoint to continue.
            </div>
          </div>
        )}

        {!needsStaticEndpoint && endpointsArray.fields.length === 0 && (
          <div className="flex items-start p-3 bg-blue-50 border border-blue-200 rounded-md">
            <Info className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              Static endpoints are optional for registered clients. Calls can use the client&apos;s registration contact.
            </div>
          </div>
        )}

        {(form.formState.errors as any)?.routingConfig?.endpoints?.message && (
          <p className="text-sm text-red-600">
            {(form.formState.errors as any).routingConfig.endpoints.message}
          </p>
        )}

        <div className="space-y-3">
          {endpointsArray.fields.map((field, idx) => {
            const isWeighted = form.watch('routingConfig.endpointSelectionMethod' as any) === 'weighted';
            return (
              <div key={field.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 bg-white p-3 border border-gray-200 rounded-lg">
                <div className="md:col-span-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Enabled</label>
                  <input
                    {...form.register(`routingConfig.endpoints.${idx}.enabled` as any)}
                    type="checkbox"
                    className="mt-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
                <div className="md:col-span-4">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Hostname</label>
                  <input
                    {...form.register(`routingConfig.endpoints.${idx}.host` as any)}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    placeholder="btt.strom2.se"
                  />
                </div>
                <div className="md:col-span-4">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Optional IP pin</label>
                  <input
                    {...form.register(`routingConfig.endpoints.${idx}.ip` as any, {
                      validate: (value: unknown) => {
                        if (value == null || value === '') return true;
                        return (
                          isValidIpv4(String(value)) ||
                          'Enter a valid IPv4 address (e.g. 192.168.1.91)'
                        );
                      },
                    })}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    placeholder="192.168.1.91"
                  />
                  {(() => {
                    const ipError = (form.formState.errors as any)?.routingConfig?.endpoints?.[idx]?.ip;
                    return ipError?.message ? (
                      <p className="mt-1 text-xs text-red-600">{ipError.message}</p>
                    ) : null;
                  })()}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Port</label>
                  <input
                    {...form.register(`routingConfig.endpoints.${idx}.port` as any, { valueAsNumber: true })}
                    type="number"
                    min={1}
                    max={65535}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    placeholder="5063"
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Weight</label>
                  <input
                    {...form.register(`routingConfig.endpoints.${idx}.weight` as any, { valueAsNumber: true })}
                    type="number"
                    min={0}
                    max={100}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm ${
                      isWeighted ? 'border-gray-300 bg-white' : 'border-gray-200 bg-white'
                    }`}
                  />
                  {!isWeighted && (
                    <div className="text-[11px] text-gray-500 mt-1">Used only when selection is Weighted.</div>
                  )}
                </div>
                <div className="md:col-span-2 flex items-end gap-2">
                  <button
                    type="button"
                    onClick={() => endpointsArray.move(idx, Math.max(0, idx - 1))}
                    disabled={idx === 0}
                    className="px-2 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 text-sm disabled:opacity-50"
                  >
                    Up
                  </button>
                  <button
                    type="button"
                    onClick={() => endpointsArray.move(idx, Math.min(endpointsArray.fields.length - 1, idx + 1))}
                    disabled={idx === endpointsArray.fields.length - 1}
                    className="px-2 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 text-sm disabled:opacity-50"
                  >
                    Down
                  </button>
                  <button
                    type="button"
                    onClick={() => endpointsArray.remove(idx)}
                    className="px-3 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                  >
                    Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* OPTIONS Ping Configuration */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
        <h4 className="font-medium text-gray-900">OPTIONS Ping Health Monitoring</h4>
        <p className="text-xs text-gray-500 mb-2">
          Send periodic SIP OPTIONS requests to monitor peer health (RFC 3261 Section 11).
        </p>

        <div className="flex items-center mb-4">
          <input
            {...form.register('routingConfig.optionsPingEnabled')}
            type="checkbox"
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label className="ml-2 block text-sm text-gray-700">
            Enable OPTIONS ping health monitoring
          </label>
        </div>

        {optionsPingEnabled && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SIP URI for OPTIONS ping
              </label>
              <input
                {...form.register('sipUri')}
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder={`sip:ping@${form.watch('domain') || 'domain.com'}`}
              />
              <p className="text-xs text-gray-500 mt-1">The SIP URI to send OPTIONS requests to. Domain is extracted for the Request-URI.</p>
              {form.formState.errors.sipUri && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.sipUri.message}</p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ping Interval (seconds)
                </label>
                <input
                  {...form.register('routingConfig.optionsPingInterval', { valueAsNumber: true })}
                  type="number"
                  min="5"
                  max="3600"
                  defaultValue={30}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">5-3600 seconds</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ping Timeout (seconds)
                </label>
                <input
                  {...form.register('routingConfig.optionsPingTimeout', { valueAsNumber: true })}
                  type="number"
                  min="1"
                  max="30"
                  defaultValue={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">1-30 seconds</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Failure Threshold
                </label>
                <input
                  {...form.register('routingConfig.optionsPingFailureThreshold', { valueAsNumber: true })}
                  type="number"
                  min="1"
                  max="10"
                  defaultValue={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Failures before marking unhealthy</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
